import {
    MONTH_NAMES,
    addDaysToKey,
    dayKey,
    detectTimeZone,
    isValidZone,
    listTimeZones,
    monthGrid,
    offsetLabel,
    timeLabel,
    zonedParts,
    zonedToUtc
} from '../../utils/calendarTime';

export const ZONE_KEY = 'franklyEventsZone';

export const TYPE_COLORS = {
    'Event Creator': '#d37e00',
    Frankly: '#88271c',
    Community: '#b9a134'
};

export const typeColor = (name) => TYPE_COLORS[name] || '#4a4a4a';

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const pad = (value) => String(value).padStart(2, '0');

export const readStoredZone = () => {
    try {
        const stored = window.localStorage.getItem(ZONE_KEY);
        if (stored && isValidZone(stored)) return stored;
    } catch {}

    return detectTimeZone();
};

export const writeStoredZone = (zone) => {
    try {
        window.localStorage.setItem(ZONE_KEY, zone);
    } catch {}
};

export const zoneLabel = (zone) => `${String(zone).replace(/_/g, ' ')} (${offsetLabel(zone)})`;

export const weekStartKey = (key) => {
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return addDaysToKey(key, -((date.getUTCDay() + 6) % 7));
};

export const shiftMonthKey = (key, delta) => {
    const [year, month] = key.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1 + delta, 1));

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-01`;
};

export const monthCells = (key) => {
    const [year, month] = key.split('-').map(Number);
    return monthGrid(year, month - 1);
};

export const readableMonth = (key) => {
    const [year, month] = key.split('-').map(Number);
    return `${MONTH_NAMES[month - 1]} ${year}`;
};

export const weekKeys = (startKey) =>
    Array.from({ length: 7 }, (_, index) => addDaysToKey(startKey, index));

export const eventDay = (event, zone) => dayKey(event.startAt, zone);

export const minutesInDay = (value, zone) => {
    const parts = zonedParts(value, zone);
    return (parts.hour * 60) + parts.minute;
};

export const eventLength = (event) => {
    if (Number.isFinite(event.lengthMins) && event.lengthMins > 0) return event.lengthMins;

    const start = Date.parse(event.startAt);
    const end = Date.parse(event.endAt);

    if (!Number.isFinite(start) || !Number.isFinite(end)) return 60;

    return Math.max(5, Math.round((end - start) / 60000));
};

export const eventEndAt = (event) => {
    if (event.endAt) return event.endAt;

    const start = Date.parse(event.startAt);
    if (!Number.isFinite(start)) return event.startAt;

    return new Date(start + (eventLength(event) * 60000)).toISOString();
};

export const eventTimeRange = (event, zone) =>
    `${timeLabel(event.startAt, zone)} to ${timeLabel(eventEndAt(event), zone)}`;

export const durationLabel = (minutes) => {
    const safe = Math.max(0, Math.round(minutes || 0));
    const hours = Math.floor(safe / 60);
    const mins = safe % 60;

    if (hours && mins) return `${hours}h ${mins}m`;
    if (hours) return `${hours}h`;

    return `${mins} mins`;
};

export const isLive = (event, now = Date.now()) => {
    const start = Date.parse(event.startAt);
    const end = Date.parse(eventEndAt(event));

    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;

    return now >= start && now < end;
};

export const isFinished = (event, now = Date.now()) => {
    const end = Date.parse(eventEndAt(event));
    return Number.isFinite(end) && now >= end;
};

export const countdownLabel = (event, now = Date.now()) => {
    const start = Date.parse(event.startAt);
    if (!Number.isFinite(start)) return '';

    const minutes = Math.round((start - now) / 60000);
    if (minutes <= 0) return 'Starting now';
    if (minutes < 60) return `In ${minutes} min${minutes === 1 ? '' : 's'}`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `In ${hours}h ${minutes % 60}m`;

    const days = Math.floor(hours / 24);
    return `In ${days} day${days === 1 ? '' : 's'}`;
};

export const readableDayLabel = (key, options = {}) => {
    const [year, month, day] = key.split('-').map(Number);

    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
        weekday: options.short ? 'short' : 'long',
        day: 'numeric',
        month: options.short ? 'short' : 'long',
        ...(options.year ? { year: 'numeric' } : {}),
        timeZone: 'UTC'
    });
};

export const readableRange = (fromKey, toKey) => {
    const format = (key) => {
        const [year, month, day] = key.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            timeZone: 'UTC'
        });
    };

    const last = addDaysToKey(toKey, -1);
    const year = last.slice(0, 4);

    return `${format(fromKey)} to ${format(last)} ${year}`;
};

export const groupByDay = (events, zone) => {
    const map = new Map();

    for (const event of events) {
        const key = eventDay(event, zone);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(event);
    }

    for (const list of map.values()) {
        list.sort((a, b) => String(a.startAt).localeCompare(String(b.startAt)));
    }

    return map;
};

const icsStamp = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

const icsEscape = (value) => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');

export const buildIcs = (event) => {
    const start = icsStamp(event.startAt);
    const end = icsStamp(eventEndAt(event));

    if (!start || !end) return null;

    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Frankly Origins//Events//EN',
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        `UID:${event.id}@franklyorigins`,
        `DTSTAMP:${icsStamp(new Date()) || start}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `SUMMARY:${icsEscape(event.name)}`,
        `LOCATION:${icsEscape(`${event.room} by ${event.owner}`)}`,
        `DESCRIPTION:${icsEscape(`Hosted by ${event.host}. Prize: ${event.win}`)}`,
        'END:VEVENT',
        'END:VCALENDAR'
    ];

    return lines.join('\r\n');
};

export const downloadIcs = (event) => {
    const body = buildIcs(event);
    if (!body) return;

    const blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.rel = 'noopener';
    link.download = `${String(event.name || 'event').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(url), 4000);
};

const eventBlurb = (event) => `Hosted by ${event.host || 'a host'}. Prize: ${event.win || 'a surprise'}`;

const eventPlace = (event) => `${event.room || 'a room'} by ${event.owner || 'an owner'}`;

export const googleCalendarUrl = (event) => {
    const start = icsStamp(event.startAt);
    const end = icsStamp(eventEndAt(event));

    if (!start || !end) return null;

    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: event.name || 'Frankly Origins event',
        dates: `${start}/${end}`,
        details: eventBlurb(event),
        location: eventPlace(event)
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const outlookDeepLink = (host, event) => {
    const start = new Date(event.startAt);
    const end = new Date(eventEndAt(event));

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

    const params = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        subject: event.name || 'Frankly Origins event',
        startdt: start.toISOString(),
        enddt: end.toISOString(),
        body: eventBlurb(event),
        location: eventPlace(event)
    });

    return `https://${host}/calendar/0/deeplink/compose?${params.toString()}`;
};

export const outlookOfficeUrl = (event) => outlookDeepLink('outlook.office.com', event);

export const yahooCalendarUrl = (event) => {
    const start = icsStamp(event.startAt);
    const end = icsStamp(eventEndAt(event));

    if (!start || !end) return null;

    const params = new URLSearchParams({
        v: '60',
        title: event.name || 'Frankly Origins event',
        st: start,
        et: end,
        desc: eventBlurb(event),
        in_loc: eventPlace(event)
    });

    return `https://calendar.yahoo.com/?${params.toString()}`;
};

export const outlookCalendarUrl = (event) => outlookDeepLink('outlook.live.com', event);

export {
    MONTH_NAMES,
    addDaysToKey,
    dayKey,
    detectTimeZone,
    isValidZone,
    listTimeZones,
    monthGrid,
    offsetLabel,
    timeLabel,
    zonedParts,
    zonedToUtc
};
